export interface JwtPayload {
  sub: string // user_id (UUID)
  role: 'ops_officer' | 'manager' | 'admin'
  username: string // display name for UI
  depositary_id: string
  exp: number
  iat: number
  // Convenience aliases used by UI components
  user_id?: string
  name?: string
}

const TOKEN_KEY = 'nolos_jwt'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
  window.dispatchEvent(new Event('nolos-auth-changed'))
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  window.dispatchEvent(new Event('nolos-auth-changed'))
}

export function decodeToken(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const raw = JSON.parse(jsonPayload)
    // Normalize: backend uses sub/username, UI uses user_id/name
    raw.user_id = raw.user_id || raw.sub
    raw.name = raw.name || raw.username
    return raw
  } catch {
    return null
  }
}

export function isTokenExpired(payload: JwtPayload): boolean {
  return Date.now() >= payload.exp * 1000
}

export function isAuthenticated(): boolean {
  const token = getToken()
  if (!token) return false
  const payload = decodeToken(token)
  if (!payload) return false
  return !isTokenExpired(payload)
}

export function getCurrentUser(): JwtPayload | null {
  const token = getToken()
  if (!token) return null
  const payload = decodeToken(token)
  if (!payload || isTokenExpired(payload)) return null
  return payload
}

export function login(token: string): JwtPayload | null {
  const payload = decodeToken(token)
  if (!payload || isTokenExpired(payload)) return null
  setToken(token)
  return payload
}

export function logout(): void {
  removeToken()
}
